import { useCallback, useEffect, useState } from "react";
import { usePlaidLink } from "react-plaid-link";
import { toast } from "sonner";
import { Landmark, RefreshCw, Unplug, Loader2 } from "lucide-react";
import { Button } from "@/components/ui/button";
import { api } from "@/lib/api";

const PlaidLauncher = ({ token, onDone, onExit }) => {
  const onSuccess = useCallback(
    async (public_token) => {
      try {
        const res = await api.plaidExchange(public_token);
        toast.success(`Bank connected — ${res.imported} transactions imported`);
        onDone?.();
      } catch (err) {
        toast.error(err?.response?.data?.detail || "Could not connect bank");
        onExit?.();
      }
    },
    [onDone, onExit]
  );

  const { open, ready } = usePlaidLink({ token, onSuccess, onExit: () => onExit?.() });

  useEffect(() => {
    if (ready) open();
  }, [ready, open]);

  return null;
};

export const BankConnect = ({ onChange }) => {
  const [status, setStatus] = useState({ configured: false, connected: false });
  const [linkToken, setLinkToken] = useState(null);
  const [busy, setBusy] = useState(false);

  const loadStatus = useCallback(async () => {
    try {
      setStatus(await api.plaidStatus());
    } catch {
      /* ignore */
    }
  }, []);

  useEffect(() => {
    loadStatus();
  }, [loadStatus]);

  const connect = async () => {
    setBusy(true);
    try {
      const { link_token } = await api.plaidLinkToken();
      setLinkToken(link_token);
    } catch (err) {
      toast.error(err?.response?.data?.detail || "Could not start Plaid. Check server keys.");
    } finally {
      setBusy(false);
    }
  };

  const sync = async () => {
    setBusy(true);
    try {
      const res = await api.plaidSync();
      toast.success(`Synced — ${res.imported} new/updated transactions`);
      onChange?.();
    } catch (err) {
      toast.error(err?.response?.data?.detail || "Sync failed");
    } finally {
      setBusy(false);
    }
  };

  const disconnect = async () => {
    await api.plaidDisconnect();
    toast.success("Bank disconnected");
    loadStatus();
  };

  const afterConnect = () => {
    setLinkToken(null);
    loadStatus();
    onChange?.();
  };

  if (!status.configured) return null;

  return (
    <>
      {status.connected ? (
        <>
          <Button
            variant="outline"
            data-testid="plaid-sync-button"
            onClick={sync}
            disabled={busy}
            className="bg-white border-gray-200 text-gray-900 hover:bg-gray-50"
          >
            {busy ? <Loader2 className="h-4 w-4 mr-1 animate-spin" /> : <RefreshCw className="h-4 w-4 mr-1" />}
            Sync Bank
          </Button>
          <Button
            variant="outline"
            data-testid="plaid-disconnect-button"
            onClick={disconnect}
            className="bg-white border-gray-200 text-gray-500 hover:bg-gray-50"
            title="Disconnect bank"
          >
            <Unplug className="h-4 w-4" />
          </Button>
        </>
      ) : (
        <Button
          data-testid="plaid-connect-button"
          onClick={connect}
          disabled={busy}
          className="bg-emerald-600 text-white hover:bg-emerald-700"
        >
          {busy ? <Loader2 className="h-4 w-4 mr-1 animate-spin" /> : <Landmark className="h-4 w-4 mr-1" />}
          Connect Bank
        </Button>
      )}
      {linkToken && <PlaidLauncher token={linkToken} onDone={afterConnect} onExit={() => setLinkToken(null)} />}
    </>
  );
};
